import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SurveyDetailsPopup from './SurveyPopup';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

const search = apiService.getBlobAssetUrl('search.svg');
const editIcon = apiService.getBlobAssetUrl('edit_12.svg');
const deleteicon = apiService.getBlobAssetUrl('delete_12.svg');
// const sendIcon = apiService.getBlobAssetUrl('send_12.svg'); // Commented out - Send feature removed

library.add(faSort, faSortUp, faSortDown);

const SurveysTable = ({ surveys, searchQuery, onEditClick, onDeleteClick, columns }) => {
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Retrieve module access from sessionStorage
  const moduleAccess = JSON.parse(sessionStorage.getItem('module_access') || '{}');
  const hasWriteAccess = moduleAccess.Surveys === 'Read/Write';

  const matchesSearchQuery = (survey, query) => {
    return Object.values(survey).some((value) => {
      if (value == null || typeof value === 'undefined') return false;
      return value.toString().toLowerCase().includes(query);
    });
  };

  const sortSurveys = (surveys, config) => {
    const sorted = [...surveys];
    const { key, direction } = config;

    if (!key) return sorted;

    sorted.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (key === 'createdOn') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else {
        aValue = aValue?.toString().toLowerCase() ?? '';
        bValue = bValue?.toString().toLowerCase() ?? '';
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = surveys.filter((survey) => matchesSearchQuery(survey, query));
    const sorted = sortSurveys(filtered, sortConfig);
    setFilteredSurveys(sorted);
  }, [surveys, searchQuery, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  return (
    <div className="mt-6" style={{ overflowX: 'auto' }}>
      <table className="survey-table w-full border border-gray-300 text-sm" id="table-2">
        <thead id="table-4">
          <tr>
            {columns
              .filter((col) => col.visible && col.key !== 'actions')
              .map((col) => {
                const isSorted = sortConfig.key === col.key;
                let sortIcon;
                if (isSorted) {
                  sortIcon = sortConfig.direction === 'asc' ? faSortUp : faSortDown;
                } else {
                  sortIcon = faSort;
                }

                return (
                  <th key={col.key} className="border px-4 py-2" id="table-12">
                    {col.label}
                    {col.key !== 'active' && col.key !== 'type' && (
                      <button
                        className="survey-sort-button"
                        onClick={() => requestSort(col.key)}
                        aria-label={`Sort by ${col.label}`}
                      >
                        <FontAwesomeIcon icon={sortIcon} />
                      </button>
                    )}
                  </th>
                );
              })}
            {hasWriteAccess && columns.find((col) => col.key === 'actions')?.visible && (
              <th className="border px-4 py-2" id="table-2">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {filteredSurveys.length > 0 ? (
            filteredSurveys.map((survey) => (
              <tr key={survey.id} className="survey-table-row">
                {columns
                  .filter((col) => col.visible && col.key !== 'actions')
                  .map((col) => {
                    let cellValue;

                    if (col.key === 'name') {
                      cellValue = (
                        <button
                          onClick={() => setSelectedSurveyId(survey.id)}
                          style={{
                            color: '#03519f',
                            textDecoration: 'underline',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          {survey.name || 'N/A'}
                        </button>
                      );
                    } else if (col.key === 'active') {
                      cellValue = survey.active ? 'Yes' : 'No';
                    } else if (col.key === 'startDate' || col.key === 'endDate') {
                      cellValue = formatDate(survey[col.key]);
                    } else if (col.key === 'responseCount') {
                      cellValue = survey.responseCount ?? 0;
                    } else {
                      cellValue = survey[col.key] ?? 'N/A';
                    }

                    let title;
                    if (typeof cellValue === 'string') {
                      title = cellValue;
                    } else if (typeof cellValue === 'number') {
                      title = String(cellValue);
                    } else {
                      title = survey[col.key]?.toString() || 'N/A';
                    }

                    return (
                      <td key={`${survey.id}-${col.key}`} id="table-13" title={title}>
                        {cellValue}
                      </td>
                    );
                  })}
                {hasWriteAccess && columns.find((col) => col.key === 'actions')?.visible && (
                  <td>
                    <div id="table-15">
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        id="table-5"
                        onClick={() => onEditClick(survey)}
                        title="Edit"
                      >
                        <img src={editIcon} alt="editicon" className="edit-img" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-800"
                        id="table-5"
                        onClick={() => onDeleteClick(survey)}
                        title="Delete"
                      >
                        <img src={deleteicon} alt="deleteicon" className="delete-img" />
                      </button>
                      {/* Send button fully commented out */}
                      {/*
                      <button
                        className="text-green-600 hover:text-green-800"
                        id="table-5"
                        onClick={() => onSendClick(survey)}
                        title="Send"
                      >
                        <img src={sendIcon} alt="sendicon" className="send-img" />
                      </button>
                      */}
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td
                id="table-16"
                colSpan={
                  hasWriteAccess
                    ? columns.filter((col) => col.visible).length
                    : columns.filter((col) => col.visible && col.key !== 'actions').length
                }
                title="No surveys found"
              >
                No surveys found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <br />
      {selectedSurveyId && (
        <SurveyDetailsPopup
          surveyId={selectedSurveyId}
          onClose={() => setSelectedSurveyId(null)}
        />
      )}
    </div>
  );
};

SurveysTable.propTypes = {
  surveys: PropTypes.array.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  // onSendClick: PropTypes.func.isRequired, // Removed from PropTypes
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      visible: PropTypes.bool.isRequired,
    })
  ).isRequired,
};

export default SurveysTable;